import type { LeverId } from "@helmr/contracts";

type Has = (lever: LeverId) => boolean;

/**
 * Every settings section and the lever(s) its page needs (D57). One table feeds the index tiles, the
 * route guards and the Settings nav, so a tile never leads to "no access" and the nav never hides a
 * section the role may open. Each entry is the lever the section's first API read checks on the server.
 */
export const SETTINGS_SECTION_LEVERS = {
  business: ["settings.business_view"],
  branches: ["settings.brands_branches_view"],
  // The page lists branches to pick from (GET /branches); setting a holiday is `staff_attendance.holidays`.
  holidays: ["settings.brands_branches_view"],
  catalogue: ["catalogue.plans_view"],
  coupons: ["catalogue.coupons_view"],
  invoicing: ["settings.invoicing_view"],
  memberships: ["settings.memberships_view"],
  "payment-accounts": ["settings.payment_accounts_view"],
  roles: ["roles.roles_view"],
  commission: ["settings.commission_view"],
  payroll: ["settings.payroll_view"],
  attendance: ["settings.attendance_view"],
  // GET /settings/pt-schedule is gated on the business settings view.
  "pt-schedule": ["settings.business_view"],
  leads: ["settings.leads_setup_view"],
  // Compound: the stages come from GET /lead-stages (leads.view); the page is part of lead setup.
  "lead-stages": ["settings.leads_setup_view", "leads.view"],
  "intake-keys": ["settings.leads_setup_view"],
  messaging: ["settings.messaging_view"],
  devices: ["devices.view"],
  audit: ["roles.audit_view"],
  // The import wizard (/settings/import). Writes need `settings.imports_edit`, matching roles needs `roles.roles_view`; the steps check those themselves.
  import: ["settings.imports_view"],
  // The go-live checklist (/settings/go-live). Ticking an item needs `settings.golive_edit`; the page checks that itself.
  "go-live": ["settings.golive_view"],
} as const satisfies Record<string, readonly LeverId[]>;

export type SettingsSection = keyof typeof SETTINGS_SECTION_LEVERS;

/** May the caller open this section? Every lever listed for it must be held. */
export const sectionAllows = (section: SettingsSection, has: Has): boolean => SETTINGS_SECTION_LEVERS[section].every((l) => has(l));

/** The Settings nav shows when any section (not just Profile) may be opened: the index shows the same tiles. */
/** `allow` for a settings `Page` or `Guarded` route. */
export const section = (key: SettingsSection) => (has: Has): boolean => sectionAllows(key, has);

export const settingsAreaAllows = (has: Has): boolean => (Object.keys(SETTINGS_SECTION_LEVERS) as SettingsSection[]).some((k) => sectionAllows(k, has));
